// ============ useTranslatedError - Traduce errori backend via errorCode ============

import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { AxiosError } from 'axios';

interface BackendErrorResponse {
  statusCode: number;
  message: string | string[];
  error?: string;
  errorCode?: string;
}

/**
 * Hook per tradurre i messaggi di errore dal backend.
 * Se l'errore contiene un errorCode, usa la traduzione i18n dal namespace 'errors'.
 * Altrimenti, usa il messaggio originale del backend come fallback.
 */
export function useTranslatedError() {
  const { t } = useTranslation('errors');

  const getErrorMessage = useCallback(
    (error: unknown, fallback?: string): string => {
      if (error instanceof AxiosError && error.response?.data) {
        const data = error.response.data as BackendErrorResponse;

        // Se c'è un errorCode, prova a tradurlo
        if (data.errorCode) {
          const translated = t(data.errorCode, { defaultValue: '' });
          if (translated) return translated;
        }

        // Fallback al messaggio backend originale
        if (Array.isArray(data.message)) {
          return data.message.join(', ');
        }
        if (data.message) {
          return data.message;
        }
      }

      if (error instanceof Error) {
        return error.message;
      }

      return fallback ?? t('INTERNAL_ERROR');
    },
    [t],
  );

  return { getErrorMessage };
}
